import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UpgradeButton } from '@/components/PaymentHandler';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';
import { 
  User, Mail, Calendar, Shield, Crown, Zap,
  CreditCard, Loader2, AlertTriangle, ExternalLink,
  Settings, Gift, CheckCircle2,
  Lock, Eye, EyeOff, Rocket, MessageSquare, Download, FileText,
  Building2
} from 'lucide-react';

import { API } from '@/utils/api';

const PLANS_DISPLAY = { starter: 'START', pro: 'PRO', agency: 'AGENCJA' };

export default function Account() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [accountType, setAccountType] = useState('personal'); // 'personal' or 'business'
  const [companyData, setCompanyData] = useState({
    company_name: '',
    nip: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'Polska',
    contact_email: '',
    contact_phone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/logowanie');
    } else if (user) {
      fetchAccount();
      fetchPaymentHistory();
      fetchBillingStatus();
    }
  }, [user, authLoading, navigate]);

  const fetchAccount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccount(res.data);
      // Set account type and company data from response
      if (res.data.account_type) {
        setAccountType(res.data.account_type);
      }
      if (res.data.company) {
        setCompanyData(res.data.company);
      }
    } catch (e) {
      toast.error('Błąd ładowania danych konta');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/billing/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(res.data.history || []);
    } catch (e) {
      // silently fail - empty history is ok
    }
  };

  const fetchBillingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/billing/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingStatus(res.data);
    } catch (e) {
      // silently fail
    }
  };

  const handleSaveProfile = async () => {
    // Validate NIP if business account
    if (accountType === 'business' && companyData.nip) {
      const nipClean = companyData.nip.replace(/[\s-]/g, '');
      if (!/^\d{10}$/.test(nipClean)) {
        toast.error('NIP musi składać się z 10 cyfr');
        return;
      }
    }
    
    // Validate required fields for business
    if (accountType === 'business') {
      if (!companyData.company_name.trim()) {
        toast.error('Podaj nazwę firmy');
        return;
      }
    }
    
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/auth/profile`, {
        account_type: accountType,
        company: accountType === 'business' ? companyData : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.updated) {
        toast.success('Profil został zapisany');
      } else {
        toast.success('Profil zapisany (bez zmian)');
      }
    } catch (e) {
      const errorMsg = e.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Błąd walidacji');
      } else {
        toast.error(errorMsg || 'Błąd zapisywania profilu');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Wylogowano pomyślnie');
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/billing/cancel`, 
        { reason: cancelReason },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Subskrypcja została anulowana');
      setCancelModalOpen(false);
      fetchAccount();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd anulowania subskrypcji');
    } finally {
      setCancelling(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('Nowe hasła nie są identyczne');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('Hasło musi mieć minimum 6 znaków');
      return;
    }
    
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, 
        { current_password: passwords.current, new_password: passwords.new },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Hasło zostało zmienione');
      setPasswordModalOpen(false);
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd zmiany hasła');
    } finally {
      setChangingPassword(false);
    }
  };

  const openStripePortal = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/billing/portal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error('Błąd otwierania portalu płatności');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F7F3EE] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#F47A3B] animate-spin" />
      </div>
    );
  }

  const planIcons = { free: Shield, pro: Zap, premium: Crown };
  const PlanIcon = planIcons[account?.plan] || Shield;
  const planColors = {
    free: 'text-zinc-500',
    pro: 'text-[#F47A3B]',
    premium: 'text-[#F47A3B]'
  };

  // Mock data for demo beta rewards
  const betaRewards = [
    { id: 1, name: 'PRO za darmo (na start)', claimed: false, available: true },
    { id: 2, name: 'Darmowy AI Scan', claimed: true, available: false },
    { id: 3, name: 'Badge Early Adopter', claimed: true, available: false },
  ];

  const tabs = [
    { id: 'profile', label: t('nav.profile'), icon: User },
    { id: 'plan', label: t('sidebar.plan'), icon: Crown },
    { id: 'payments', label: t('account.payments', 'Płatności'), icon: CreditCard },
    { id: 'beta', label: t('account.betaBonuses', 'Beta & bonusy'), icon: Gift },
    { id: 'settings', label: t('sidebar.settings'), icon: Settings },
  ];

  return (
    <PanelLayout title={t('sidebar.plan')} subtitle={t('account.subtitle', 'Zarządzaj swoim kontem i subskrypcją')}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F47A3B] to-[#F9A66C] flex items-center justify-center shadow-lg shadow-[#F47A3B]/20">
            <User className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-[22px] font-bold text-zinc-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{account?.name || account?.email}</h1>
            <p className="text-zinc-500 text-[14px]">{account?.email}</p>
          </div>
          <Badge className={`ml-auto ${planColors[account?.plan]} bg-zinc-100 text-lg px-4 py-2`}>
            <PlanIcon className="w-5 h-5 mr-2" />
            {account?.plan_name || 'FREE'}
          </Badge>
        </div>

        {/* Tabs — underline style */}
        <div className="flex gap-6 mb-8 border-b border-zinc-200" data-testid="account-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`account-tab-${tab.id}`}
                className={`flex items-center gap-2 px-1 pb-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#F47A3B]'
                    : 'text-zinc-400 hover:text-zinc-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F47A3B] rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* === PROFIL (Prywatne/Firmowe) === */}
          {activeTab === 'profile' && (
            <>
              <div className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#F47A3B]" />
                  Typ konta
                </h2>
                
                {/* Account Type Selection */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => setAccountType('personal')}
                    className={`p-6 rounded-xl border-2 text-left transition-all ${
                      accountType === 'personal'
                        ? 'border-[#F47A3B] bg-orange-50'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      accountType === 'personal' ? 'bg-[#F47A3B]' : 'bg-zinc-100'
                    }`}>
                      <User className={`w-6 h-6 ${accountType === 'personal' ? 'text-white' : 'text-zinc-500'}`} />
                    </div>
                    <h3 className="font-semibold text-zinc-900 mb-1">Konto prywatne</h3>
                    <p className="text-sm text-zinc-500">Dla osób fizycznych, hobbyistów</p>
                  </button>
                  
                  <button
                    onClick={() => setAccountType('business')}
                    className={`p-6 rounded-xl border-2 text-left transition-all ${
                      accountType === 'business'
                        ? 'border-[#F47A3B] bg-orange-50'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      accountType === 'business' ? 'bg-[#F47A3B]' : 'bg-zinc-100'
                    }`}>
                      <Building2 className={`w-6 h-6 ${accountType === 'business' ? 'text-white' : 'text-zinc-500'}`} />
                    </div>
                    <h3 className="font-semibold text-zinc-900 mb-1">Konto firmowe</h3>
                    <p className="text-sm text-zinc-500">Dla firm, faktury VAT</p>
                  </button>
                </div>

                {/* Company Data Form (visible when business selected) */}
                {accountType === 'business' && (
                  <div className="border-t border-zinc-200 pt-6">
                    <h3 className="text-lg font-medium text-zinc-900 mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-zinc-500" />
                      Dane firmy
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <Label className="text-zinc-500">Nazwa firmy *</Label>
                        <Input
                          value={companyData.company_name}
                          onChange={(e) => setCompanyData(d => ({ ...d, company_name: e.target.value }))}
                          placeholder="np. Jan Kowalski Hodowla Psów"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-zinc-500">NIP *</Label>
                        <Input
                          value={companyData.nip}
                          onChange={(e) => setCompanyData(d => ({ ...d, nip: e.target.value }))}
                          placeholder="1234567890"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-zinc-500">Kraj</Label>
                        <Input
                          value={companyData.country}
                          onChange={(e) => setCompanyData(d => ({ ...d, country: e.target.value }))}
                          placeholder="Polska"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label className="text-zinc-500">Adres (ulica i numer) *</Label>
                        <Input
                          value={companyData.address}
                          onChange={(e) => setCompanyData(d => ({ ...d, address: e.target.value }))}
                          placeholder="ul. Główna 1"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-zinc-500">Kod pocztowy *</Label>
                        <Input
                          value={companyData.postal_code}
                          onChange={(e) => setCompanyData(d => ({ ...d, postal_code: e.target.value }))}
                          placeholder="00-000"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-zinc-500">Miasto *</Label>
                        <Input
                          value={companyData.city}
                          onChange={(e) => setCompanyData(d => ({ ...d, city: e.target.value }))}
                          placeholder="Warszawa"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-zinc-500">Email kontaktowy</Label>
                        <Input
                          type="email"
                          value={companyData.contact_email}
                          onChange={(e) => setCompanyData(d => ({ ...d, contact_email: e.target.value }))}
                          placeholder="kontakt@firma.pl"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-zinc-500">Telefon kontaktowy</Label>
                        <Input
                          type="tel"
                          value={companyData.contact_phone}
                          onChange={(e) => setCompanyData(d => ({ ...d, contact_phone: e.target.value }))}
                          placeholder="+48 123 456 789"
                          className="bg-zinc-50 border-zinc-200 mt-1"
                        />
                      </div>
                    </div>
                    
                    <p className="text-xs text-zinc-400 mt-4">
                      * Dane firmy będą używane w stopkach, mailach i raportach
                    </p>
                  </div>
                )}

                {/* Save Button */}
                <div className="mt-6 pt-6 border-t border-zinc-200">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="bg-[#F47A3B] hover:bg-[#E06A2B]"
                  >
                    {savingProfile ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Zapisz profil
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* === TWÓJ PLAN === */}
          {activeTab === 'plan' && (
            <>
              <div className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-6 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#F47A3B]" />
                  Twoje konto
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-50" data-testid="current-plan-info">
                      <span className="text-zinc-500">Aktualny plan</span>
                      <span className={`font-bold ${billingStatus?.plan ? planColors[billingStatus.plan] || 'text-[#F47A3B]' : planColors[account?.plan]}`}>
                        {billingStatus?.plan ? PLANS_DISPLAY[billingStatus.plan] || billingStatus.plan.toUpperCase() : (account?.plan_name || 'FREE')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-50" data-testid="subscription-status">
                      <span className="text-zinc-500">Status</span>
                      <Badge className={billingStatus?.is_trial ? 'bg-blue-100 text-blue-700' : billingStatus?.can_access ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {billingStatus?.is_trial ? `Trial (${billingStatus.days_left} dni)` : billingStatus?.status === 'active' ? 'Aktywny' : billingStatus?.status === 'canceled' ? 'Anulowany' : billingStatus?.status === 'expired' ? 'Wygasły' : 'Bezpłatny'}
                      </Badge>
                    </div>
                    {billingStatus?.status === 'active' && billingStatus?.period_end && (
                      <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-50" data-testid="next-payment-date">
                        <span className="text-zinc-500">Następna płatność</span>
                        <span className="text-zinc-900">{new Date(billingStatus.period_end).toLocaleDateString('pl-PL')}</span>
                      </div>
                    )}
                    {billingStatus?.is_trial && billingStatus?.trial_end_at && (
                      <div className="flex items-center justify-between p-4 rounded-xl bg-blue-50" data-testid="trial-end-date">
                        <span className="text-zinc-500">Koniec triala</span>
                        <span className="text-blue-700 font-medium">{new Date(billingStatus.trial_end_at).toLocaleDateString('pl-PL')}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col justify-center gap-3">
                    {(!billingStatus?.plan || billingStatus?.is_trial || billingStatus?.status === 'expired' || billingStatus?.status === 'canceled') ? (
                      <>
                        <UpgradeButton plan="pro" className="w-full" />
                        <Button 
                          variant="outline"
                          onClick={() => navigate('/panel/cennik')}
                          className="w-full"
                          data-testid="view-plans-btn"
                        >
                          Zobacz wszystkie plany
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          onClick={openStripePortal}
                          className="w-full bg-zinc-100 hover:bg-zinc-200 text-zinc-900"
                          data-testid="manage-payments-btn"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Zarządzaj płatnościami
                        </Button>
                        <Button 
                          variant="ghost"
                          onClick={() => setCancelModalOpen(true)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid="cancel-subscription-btn"
                        >
                          Zatrzymaj promocję
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Aktywne usługi */}
              <div className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm">
                <h3 className="text-lg font-semibold text-zinc-900 mb-4">Aktywne usługi</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-[#F47A3B]" />
                      </div>
                      <div>
                        <p className="text-zinc-900 font-medium">Plan {account?.plan_name || 'FREE'}</p>
                        <p className="text-zinc-500 text-sm">Dostęp do podstawowych funkcji</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">Aktywny</Badge>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* === PŁATNOŚCI === */}
          {activeTab === 'payments' && (
            <>
              <div className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-zinc-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#F47A3B]" />
                    Historia płatności
                  </h2>
                  <Button variant="outline" size="sm" onClick={openStripePortal}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Portal Stripe
                  </Button>
                </div>
                
                {paymentHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-zinc-200">
                          <th className="text-left py-3 px-4 text-zinc-500 font-medium">Data</th>
                          <th className="text-left py-3 px-4 text-zinc-500 font-medium">Usługa</th>
                          <th className="text-left py-3 px-4 text-zinc-500 font-medium">Kwota</th>
                          <th className="text-left py-3 px-4 text-zinc-500 font-medium">Status</th>
                          <th className="text-right py-3 px-4 text-zinc-500 font-medium">Faktura</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentHistory.map((payment) => (
                          <tr key={payment.id} className="border-b border-zinc-200">
                            <td className="py-4 px-4 text-zinc-900">{payment.date}</td>
                            <td className="py-4 px-4 text-zinc-900">{payment.service}</td>
                            <td className="py-4 px-4 text-zinc-900">{payment.amount}</td>
                            <td className="py-4 px-4">
                              <Badge className="bg-emerald-100 text-emerald-700">Opłacone</Badge>
                            </td>
                            <td className="py-4 px-4 text-right">
                              <Button variant="ghost" size="sm" className="text-[#F47A3B]">
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-zinc-400 mx-auto mb-3" />
                    <p className="text-zinc-500">Brak historii płatności</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* === BETA & BONUSY === */}
          {activeTab === 'beta' && (
            <>
              <div className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm" id="bonusy">
                <h2 className="text-xl font-semibold text-zinc-900 mb-6 flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-[#F47A3B]" />
                  Program Beta Tester
                </h2>
                
                <div className="p-4 rounded-xl bg-orange-50 border border-orange-200 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-[#F47A3B]" />
                    </div>
                    <div>
                      <p className="text-zinc-900 font-semibold">Jesteś Beta Testerem!</p>
                      <p className="text-orange-600 text-sm">Dziękujemy za pomoc w rozwijaniu ApisAI</p>
                    </div>
                    <Badge className="ml-auto bg-orange-100 text-orange-600">
                      Early Adopter
                    </Badge>
                  </div>
                </div>

                <h3 className="text-lg font-medium text-zinc-900 mb-4">Twoje nagrody</h3>
                <div className="space-y-3">
                  {betaRewards.map((reward) => (
                    <div 
                      key={reward.id} 
                      className={`flex items-center justify-between p-4 rounded-xl ${
                        reward.claimed ? 'bg-zinc-50' : 'bg-emerald-50 border border-emerald-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Gift className={`w-5 h-5 ${reward.claimed ? 'text-zinc-400' : 'text-emerald-500'}`} />
                        <span className={reward.claimed ? 'text-zinc-500' : 'text-zinc-900'}>
                          {reward.name}
                        </span>
                      </div>
                      {reward.claimed ? (
                        <Badge className="bg-zinc-200 text-zinc-500">Wykorzystane</Badge>
                      ) : reward.available ? (
                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600">
                          Odbierz
                        </Button>
                      ) : (
                        <Badge className="bg-zinc-200 text-zinc-500">Niedostępne</Badge>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-zinc-200">
                  <h3 className="text-lg font-medium text-zinc-900 mb-4">Podziel się opinią</h3>
                  <a 
                    href="https://forms.gle/your-feedback-form" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-blue-500 hover:bg-blue-600">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Wypełnij ankietę beta testera
                    </Button>
                  </a>
                  <p className="text-zinc-500 text-sm text-center mt-2">
                    Wypełnienie ankiety odblokowuje dodatkowe nagrody!
                  </p>
                </div>
              </div>
            </>
          )}

          {/* === USTAWIENIA === */}
          {activeTab === 'settings' && (
            <>
              <div className="bg-white rounded-2xl p-6 border border-zinc-200 shadow-sm">
                <h2 className="text-xl font-semibold text-zinc-900 mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#F47A3B]" />
                  Dane konta
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-50">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-zinc-400" />
                      <div>
                        <p className="text-zinc-500 text-sm">Email</p>
                        <p className="text-zinc-900">{account?.email}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-50">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-zinc-400" />
                      <div>
                        <p className="text-zinc-500 text-sm">Konto utworzone</p>
                        <p className="text-zinc-900">
                          {account?.created_at ? new Date(account.created_at).toLocaleDateString('pl-PL') : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-50">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-zinc-400" />
                      <div>
                        <p className="text-zinc-500 text-sm">Metoda logowania</p>
                        <p className="text-zinc-900">
                          {account?.auth_provider === 'google' ? 'Google' : 'Email i hasło'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {account?.auth_provider !== 'google' && (
                  <div className="mt-6 pt-6 border-t border-zinc-200">
                    <Button 
                      variant="outline" 
                      onClick={() => setPasswordModalOpen(true)}
                      className="w-full border-zinc-200"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Zmień hasło
                    </Button>
                  </div>
                )}
              </div>

              {/* Danger Zone */}
              <div className="bg-white rounded-2xl p-6 border border-red-200 shadow-sm">
                <h2 className="text-xl font-semibold text-red-500 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Strefa niebezpieczna
                </h2>
                <p className="text-zinc-500 mb-4">
                  Usunięcie konta jest nieodwracalne. Wszystkie dane zostaną trwale usunięte.
                </p>
                <details className="text-xs text-zinc-400">
                  <summary className="cursor-pointer hover:text-zinc-600 transition-colors">Chce usunac konto...</summary>
                  <div className="mt-3">
                    <Button 
                      variant="outline"
                      onClick={() => setDeleteModalOpen(true)}
                      className="text-red-500 border-red-200 hover:bg-red-50"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Usuń konto
                    </Button>
                  </div>
                </details>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Cancel Subscription Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900">
          <DialogHeader>
            <DialogTitle className="text-xl">Zatrzymaj system</DialogTitle>
            <DialogDescription className="text-zinc-500 pt-4">
              <p className="mb-4">Po zatrzymaniu:</p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li>Twoje dane hodowli pozostaną zapisane</li>
                <li>System przestanie zdobywać klientów</li>
                <li>Możesz wrócić w każdej chwili</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm text-zinc-500 mb-2 block">
              Powiedz nam dlaczego anulujesz (opcjonalnie)
            </label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Twoja opinia pomoże nam się rozwijać..."
              className="bg-zinc-50 border-zinc-200"
            />
          </div>
          <DialogFooter className="gap-3">
            <Button variant="ghost" onClick={() => setCancelModalOpen(false)}>
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelling}
              className="bg-red-500 hover:bg-red-600"
            >
              {cancelling ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Potwierdź anulowanie'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900">
          <DialogHeader>
            <DialogTitle className="text-xl">Zmień hasło</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-500">Aktualne hasło</Label>
              <div className="relative">
                <Input
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwords.current}
                  onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                  className="bg-zinc-50 border-zinc-200 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(s => ({ ...s, current: !s.current }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-500">Nowe hasło</Label>
              <Input
                type="password"
                value={passwords.new}
                onChange={(e) => setPasswords(p => ({ ...p, new: e.target.value }))}
                className="bg-zinc-50 border-zinc-200"
              />
            </div>
            <div>
              <Label className="text-zinc-500">Potwierdź nowe hasło</Label>
              <Input
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords(p => ({ ...p, confirm: e.target.value }))}
                className="bg-zinc-50 border-zinc-200"
              />
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="ghost" onClick={() => setPasswordModalOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {changingPassword ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Zmień hasło'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-red-500">
              <AlertTriangle className="w-6 h-6" />
              Usuń konto
            </DialogTitle>
            <DialogDescription className="text-zinc-500 pt-4">
              <p className="mb-4 text-red-500 font-medium">Ta operacja jest nieodwracalna!</p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li>Wszystkie Twoje dane zostaną trwale usunięte</li>
                <li>Twoje hodowle i psy zostaną usunięte</li>
                <li>Aktywna subskrypcja zostanie anulowana</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm text-zinc-500 mb-2 block">
              Wpisz <span className="text-red-500 font-mono">USUŃ</span> aby potwierdzić
            </label>
            <Input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="USUŃ"
              className="bg-zinc-50 border-zinc-200"
            />
          </div>
          <DialogFooter className="gap-3">
            <Button variant="ghost" onClick={() => { setDeleteModalOpen(false); setDeleteConfirm(''); }}>
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (deleteConfirm !== 'USUŃ') {
                  toast.error('Wpisz USUŃ aby potwierdzić');
                  return;
                }
                setDeleting(true);
                try {
                  const token = localStorage.getItem('token');
                  await axios.delete(`${API}/account`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  toast.success('Konto zostało usunięte');
                  logout();
                  navigate('/');
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Błąd usuwania konta');
                } finally {
                  setDeleting(false);
                }
              }}
              disabled={deleting || deleteConfirm !== 'USUŃ'}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Usuń konto na zawsze'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PanelLayout>
  );
}
